<script setup lang="ts" generic="TValues extends z.ZodType<any>">
import { computed, useTemplateRef, defineAsyncComponent } from 'vue'
import { z } from 'zod'
import UiForm from './UiForm.vue'
import UiFormField from './UiFormField.vue'
import UiFormSectionHeading from './UiFormSectionHeading.vue'
import type { UiFormBuilderProps } from './UiFormBuilder.types'

// Lazy load field components (only loaded when used)
const UiInputField = defineAsyncComponent(() => import('./UiInputField.vue'))
const UiSelect = defineAsyncComponent(() => import('./UiSelect.vue'))
const UiCombobox = defineAsyncComponent(() => import('./UiCombobox.vue'))
const UiTextarea = defineAsyncComponent(() => import('./UiTextarea.vue'))
const UiPhoneInput = defineAsyncComponent(() => import('./UiPhoneInput.vue'))
const UiMultiSelectChips = defineAsyncComponent(() => import('./UiMultiSelectChips.vue'))
const UiCurrencyInput = defineAsyncComponent(() => import('./UiCurrencyInput.vue'))
const UiRichTextEditor = defineAsyncComponent(() => import('./UiRichTextEditor.vue'))
const UiFileUpload = defineAsyncComponent(() => import('./UiFileUpload.vue'))
const UiAvatarUpload = defineAsyncComponent(() => import('./UiAvatarUpload.vue'))
const UiDatePicker = defineAsyncComponent(() => import('./UiDatePicker.vue'))
const UiCheckbox = defineAsyncComponent(() => import('./UiCheckbox.vue'))

const props = withDefaults(defineProps<UiFormBuilderProps<TValues>>(), {
  gap: '6',
  initialValues: undefined,
})

const model = defineModel<z.infer<TValues>>()
const isDirty = defineModel<boolean>('dirty', { default: false })
const isValid = defineModel<boolean>('valid', { default: false })
const wasTouched = defineModel<boolean>('touched', { default: false })

const formRef = useTemplateRef('formRef')

function onSubmit(values: z.infer<TValues>) {
  emit('submit', values)
}
function onInvalid(ctx: unknown) {
  // UiForm emits `invalid-submit` with an `errors` object, but is typed as unknown
  const errors = ctx as Record<string, string | undefined>
  emit('invalid', errors)
}

const emit = defineEmits<{ (e: 'submit', values: z.infer<TValues>): void, (e: 'invalid', errors: Record<string, string | undefined>): void }>()

const rowClass = computed(() => `flex flex-col sm:flex-row gap-${props.gap}`)
const colClass = computed(() => `flex-1 min-w-0 gap-${props.gap}`)

defineExpose({
  setErrors: formRef.value?.setErrors,
  setFieldError: formRef.value?.setFieldError,
  validate: (shouldScrollToFirstError?: boolean) => formRef.value?.validate(shouldScrollToFirstError),
  clearErrors: () => formRef.value?.clearErrors(),
  forceClear: () => formRef.value?.forceClear(),
  resetForm: () => formRef.value?.resetForm(),
})
</script>

<template>
  <UiForm ref="formRef" :initial-values="props.initialValues" v-model="model" :schema="props.schema" @submit="onSubmit" @invalid-submit="onInvalid" :scroll-to-first-error="props.scrollToFirstError" :server-errors="props.serverErrors" v-model:dirty="isDirty" v-model:valid="isValid" v-model:touched="wasTouched">
    <div class="flex flex-col gap-6">
      <div v-for="(row, rIdx) in props.rows" :key="rIdx" :class="rowClass">
        <div v-for="(col, cIdx) in row.columns" :key="cIdx" :class="colClass">
          <UiFormSectionHeading v-if="'heading' in col" :text="col.heading" :subtitle="col.subtitle" :divider="col.divider" />
          <UiFormField v-else :name="col.name" :label="col.label" :labelCapitalize="col.labelCapitalize" :hint="col.hint" :inlineHint="col.inlineHint" :size="col.size" :locked="col.locked" :proBadge="col.proBadge" :disabled="col.disabled" :type="col.type">
            <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error, disabled }">
              <component 
              :is="
                col.type === 'custom' ? col.component :
                col.type === 'select' ? UiSelect :
                col.type === 'combobox' ? UiCombobox :
                col.type === 'textarea' ? UiTextarea :
                col.type === 'phone' ? UiPhoneInput :
                col.type === 'multiselect-chips' ? UiMultiSelectChips :
                col.type === 'currency' ? UiCurrencyInput :
                col.type === 'richtext' ? UiRichTextEditor :
                col.type === 'file-upload' ? UiFileUpload :
                col.type === 'avatar-upload' ? UiAvatarUpload :
                col.type === 'date' ? UiDatePicker :
                col.type === 'date-range' ? UiDatePicker :
                col.type === 'checkbox' ? UiCheckbox : UiInputField
              "
                :modelValue="modelValue as any"
                @update:modelValue="onUpdateModelValue"
                @validate="onValidate"
                v-bind="{ ...(col.props || {}), id, name: col.name, hint: col.hint, hintContent: col.hintContent, size: col.size, invalid, error: error || undefined, disabled, range: col.type === 'date-range' }"
              />
            </template>
          </UiFormField>
        </div>
      </div>
      <slot name="actions" />
    </div>
    <template #debug="{ meta, errors, values }">
        <slot name="debug" :meta="meta" :errors="errors" :values="values" />
    </template>
  </UiForm>
</template>

